import { SUCCESS_CODE } from '@/constants'
import { MockMethod } from 'vite-plugin-mock'

const timeout = 400

/** 驾驶舱 mock 默认基准坐标（北京，仅用于本地 mock 数据偏移） */
const BASE_LNG = 116.397428
const BASE_LAT = 39.90923

const vehicleTypes = ['school_bus', 'bus', 'ride_hailing', 'rental'] as const
const vehicleLabels: Record<string, string> = {
  school_bus: '通学公交',
  bus: '公交车',
  ride_hailing: '网约车',
  rental: '租赁'
}

function seedFromRange(start?: string, end?: string, granularity?: string) {
  const s = start ? new Date(start).getTime() : Date.now() - 86400000 * 30
  const e = end ? new Date(end).getTime() : Date.now()
  const span = Math.max(1, Math.floor((e - s) / 86400000))
  const factor =
    granularity === 'year' ? 12 : granularity === 'quarter' ? 4 : granularity === 'month' ? 2 : 1
  return Math.min(12, Math.max(3, Math.floor(span / 7) * factor))
}

function buildTrend(name: string, points: number, base: number) {
  const now = Date.now()
  return {
    name,
    points: Array.from({ length: points }, (_, i) => ({
      time: new Date(now - (points - i) * 86400000).toISOString().slice(0, 19).replace('T', ' '),
      value: Math.max(0, Math.round(base + Math.sin(i / 2) * base * 0.15 + (Math.random() - 0.5) * base * 0.08))
    }))
  }
}

/** mock 预警列表（可被 handled 接口改写） */
const mockAlerts = [
  {
    id: 'a1',
    level: 'high' as const,
    title: '订单支付失败：维修订单 RO20260315001',
    bizLine: '维修',
    alertTime: new Date(Date.now() - 120000).toISOString().slice(0, 19).replace('T', ' '),
    handled: false,
    routeName: 'repairOrderManage'
  },
  {
    id: 'a2',
    level: 'medium' as const,
    title: '订单异常：租赁订单超时未还车',
    bizLine: '租赁',
    alertTime: new Date(Date.now() - 360000).toISOString().slice(0, 19).replace('T', ' '),
    handled: false,
    routeName: 'PreOrderManage'
  },
  {
    id: 'a3',
    level: 'low' as const,
    title: 'GPS 信号弱：京A·8821',
    bizLine: vehicleLabels.rental,
    alertTime: new Date(Date.now() - 720000).toISOString().slice(0, 19).replace('T', ' '),
    handled: true
  },
  {
    id: 'a4',
    level: 'high' as const,
    title: '支付失败：套餐购买订单',
    bizLine: '套餐',
    alertTime: new Date(Date.now() - 900000).toISOString().slice(0, 19).replace('T', ' '),
    handled: false,
    routeName: 'packageManage'
  }
]

export default [
  {
    url: '/mock/cockpit/overview',
    method: 'get',
    timeout,
    response: () => ({
      code: SUCCESS_CODE,
      data: {
        serverTime: new Date().toISOString().slice(0, 19).replace('T', ' '),
        modules: ['dashboard', 'map', 'revenue', 'order', 'alerts']
      }
    })
  },
  {
    url: '/mock/cockpit/dashboard',
    method: 'get',
    timeout,
    response: ({ query }) => {
      const points = seedFromRange(query.startDate, query.endDate, query.granularity)
      const jitter = Math.floor(Math.random() * 50)
      return {
        code: SUCCESS_CODE,
        data: {
          cards: [
            { key: 'revenueToday', label: '当日应收(元)', value: 128600 + jitter, trend: 5.2 },
            { key: 'orderToday', label: '当日订单', value: 342 + jitter, trend: 3.1 },
            { key: 'orderActive', label: '进行中订单', value: 89 + (jitter % 10), trend: -1.2 },
            { key: 'payFail', label: '支付失败', value: 7 + (jitter % 5), trend: -8.0 },
            { key: 'vehiclesOnline', label: '在线车辆', value: 156 + jitter, trend: 2.4 },
            { key: 'alertPending', label: '待处理预警', value: 12 + (jitter % 3), trend: 0 }
          ],
          trends: [
            buildTrend('订单趋势', points, 280),
            buildTrend('营收趋势', points, 98000)
          ],
          distribution: [
            { name: '维修', value: 120 + jitter },
            { name: '保养', value: 86 },
            { name: '租赁', value: 64 },
            { name: '洗车', value: 42 },
            { name: '年检', value: 30 }
          ]
        }
      }
    }
  },
  {
    url: '/mock/cockpit/map/vehicles',
    method: 'get',
    timeout,
    response: () => ({
      code: SUCCESS_CODE,
      data: Array.from({ length: 16 }, (_, i) => {
        const type = vehicleTypes[i % vehicleTypes.length]
        return {
          vehicleId: `v-${1000 + i}`,
          vehicleType: type,
          lng: BASE_LNG + (Math.random() - 0.5) * 0.15,
          lat: BASE_LAT + (Math.random() - 0.5) * 0.12,
          status: i % 3 === 0 ? 'idle' : 'running',
          plateNo: `京A·${String(1000 + i).slice(-4)}`,
          updatedTime: new Date().toISOString().slice(0, 19).replace('T', ' ')
        }
      })
    })
  },
  {
    url: '/mock/cockpit/map/tracks',
    method: 'get',
    timeout,
    response: ({ query }) => {
      const id = Number(String(query.vehicleId || 'v-1000').replace(/\D/g, '')) || 1000
      const offset = (id % 10) * 0.001
      return {
        code: SUCCESS_CODE,
        data: Array.from({ length: 20 }, (_, i) => ({
          lng: BASE_LNG + offset + i * 0.003,
          lat: BASE_LAT + offset + Math.sin(i / 3) * 0.01,
          time: new Date(Date.now() - (20 - i) * 60000).toISOString().slice(0, 19).replace('T', ' ')
        }))
      }
    }
  },
  {
    url: '/mock/cockpit/revenue/summary',
    method: 'get',
    timeout,
    response: ({ query }) => {
      const points = seedFromRange(query.startDate, query.endDate, query.granularity)
      const mul = points / 6
      return {
        code: SUCCESS_CODE,
        data: {
          dayAmount: 128600,
          monthAmount: 2860000 * mul,
          quarterAmount: 8120000 * mul,
          yearAmount: 32500000 * mul,
          compareRate: 6.8,
          series: [buildTrend('应收', points, 85000)]
        }
      }
    }
  },
  {
    url: '/mock/cockpit/order/summary',
    method: 'get',
    timeout,
    response: ({ query }) => {
      const points = seedFromRange(query.startDate, query.endDate, query.granularity)
      return {
        code: SUCCESS_CODE,
        data: {
          totalCount: 1284,
          statusDistribution: [
            { status: '待支付', count: 86 },
            { status: '进行中', count: 142 },
            { status: '已完成', count: 980 },
            { status: '已取消', count: 56 },
            { status: '售后中', count: 20 }
          ],
          cumulative: [buildTrend('累计订单', points, 900)]
        }
      }
    }
  },
  {
    url: '/mock/cockpit/alerts/recent',
    method: 'get',
    timeout,
    response: () => ({
      code: SUCCESS_CODE,
      data: mockAlerts
    })
  },
  {
    url: '/mock/cockpit/alerts/handled',
    method: 'post',
    timeout: 200,
    response: ({ body }: { body: { id?: string } }) => {
      const id = body?.id
      if (id) {
        const hit = mockAlerts.find((a) => a.id === id)
        if (hit) hit.handled = true
      }
      return { code: SUCCESS_CODE, data: true }
    }
  }
] as MockMethod[]
